import { useState } from 'react';
import { Send, Loader2, Check, AlertCircle } from 'lucide-react';
import { extractContent, ingestMemory } from '../lib/api';
import type { Extraction } from '../lib/types';

interface CaptureResult {
  extraction: Extraction;
  memoryId?: string;
}

export function QuickCapture() {
  const [content, setContent] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<CaptureResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || isProcessing) return;

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      // Extract insights from the content
      const extractResponse = await extractContent(content, 'dashboard');

      if (!extractResponse.success || !extractResponse.data) {
        throw new Error(extractResponse.error || 'Extraction failed');
      }

      // Also ingest to memory
      const ingestResponse = await ingestMemory(content, 'dashboard:capture');

      setResult({
        extraction: extractResponse.data.extraction,
        memoryId: ingestResponse.data?.memory_id,
      });

      setContent('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="card">
        <h2 className="text-lg font-semibold mb-3">Quick Capture</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Capture a thought, decision, or idea..."
            className="textarea min-h-[120px]"
            disabled={isProcessing}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!content.trim() || isProcessing}
              className="btn-primary flex items-center gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Capture
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Error display */}
      {error && (
        <div className="card border-red-900 bg-red-950/50">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="font-medium text-red-400">Capture failed</p>
              <p className="text-sm text-red-300/70 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Result display */}
      {result && (
        <div className="card border-green-900 bg-green-950/30">
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-green-500 mt-0.5" />
            <div className="flex-1 space-y-3">
              <p className="font-medium text-green-400">Captured and processed</p>

              {result.extraction.decisions.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
                    Decisions
                  </p>
                  <ul className="space-y-1">
                    {result.extraction.decisions.map((d, i) => (
                      <li key={i} className="text-sm text-gray-300">
                        {d.decision}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.extraction.action_items.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
                    Action Items
                  </p>
                  <ul className="space-y-1">
                    {result.extraction.action_items.map((a, i) => (
                      <li key={i} className="text-sm text-gray-300">
                        {a.task}
                        {a.priority && (
                          <span className="ml-2 text-xs text-brain-400">({a.priority})</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.extraction.insights.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
                    Insights
                  </p>
                  <ul className="space-y-1">
                    {result.extraction.insights.map((ins, i) => (
                      <li key={i} className="text-sm text-gray-300">
                        {ins.insight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.extraction.entities.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
                    Entities
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {result.extraction.entities.map((e, i) => (
                      <span
                        key={i}
                        className="text-xs bg-gray-800 text-gray-300 px-2 py-1 rounded"
                      >
                        {e.name}
                        <span className="text-gray-500 ml-1">({e.type})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
